/**
 * Composant liste des patients
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientService } from '@/shared/services/patientService';
import { Patient } from '@/shared/types/patient';
import { useAuth } from '@/features/auth/contexts/AuthContext';

interface PatientListProps {
  onSelectPatient?: (patient: Patient) => void;
  onCreateClick?: () => void;
}

export function PatientList({ onSelectPatient, onCreateClick }: PatientListProps) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadPatients = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await patientService.list({ search, page, size: 20 });
      setPatients(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, [page, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const canCreate = user?.profil === 'admin' || user?.profil === 'cadre' || user?.profil === 'médecin';

  if (isLoading && patients.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="row mb-4 align-items-center">
        <div className="col">
          <h1 className="text-gradient fw-bold mb-1">
            <i className="bi bi-people-fill me-2"></i>
            Patients
          </h1>
          <p className="text-muted mb-0">
            <i className="bi bi-database me-1"></i>
            {total} patient{total > 1 ? 's' : ''} au total
          </p>
        </div>
        {canCreate && onCreateClick && (
          <div className="col-auto">
            <button onClick={onCreateClick} className="btn btn-primary shadow-glow">
              <i className="bi bi-plus-circle me-2"></i>
              Nouveau patient
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="row mb-4">
        <div className="col-12 col-md-6">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              placeholder="Rechercher (nom, prénom, IPP)..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center fade-in" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>{error}</div>
        </div>
      )}

      {/* Table Card */}
      <div className="card fade-in">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-sexy mb-0">
              <thead>
                <tr>
                  <th>
                    <i className="bi bi-person me-1"></i>
                    Patient
                  </th>
                  <th>
                    <i className="bi bi-hash me-1"></i>
                    IPP
                  </th>
                  <th>
                    <i className="bi bi-calendar-event me-1"></i>
                    Date naissance
                  </th>
                  <th>
                    <i className="bi bi-hospital me-1"></i>
                    Service
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {patients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-5">
                      <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                      Aucun patient trouvé
                    </td>
                  </tr>
                ) : (
                  patients.map((patient) => (
                    <tr
                      key={patient.id}
                      onClick={() => onSelectPatient?.(patient)}
                      className={onSelectPatient ? 'cursor-pointer' : ''}
                    >
                      <td>
                        <div className="fw-semibold">
                          {patient.prenom} {patient.nom}
                        </div>
                      </td>
                      <td>
                        <code className="text-primary">{patient.ipp}</code>
                      </td>
                      <td className="text-muted">
                        {patient.date_naissance
                          ? new Date(patient.date_naissance).toLocaleDateString('fr-FR')
                          : 'N/A'}
                      </td>
                      <td>
                        <span className="badge bg-primary-subtle text-primary">
                          {patient.service_actuel || 'N/A'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(`/patients/${patient.id}/timeline`)}
                          title="Voir le dossier"
                        >
                          <i className="bi bi-clock-history me-1"></i>
                          Dossier
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {total > 20 && (
        <nav className="mt-4 d-flex justify-content-center">
          <ul className="pagination">
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="page-link"
              >
                <i className="bi bi-chevron-left"></i>
                Précédent
              </button>
            </li>
            <li className="page-item disabled">
              <span className="page-link">
                Page {page} / {Math.ceil(total / 20)}
              </span>
            </li>
            <li className={`page-item ${page >= Math.ceil(total / 20) ? 'disabled' : ''}`}>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / 20)}
                className="page-link"
              >
                Suivant
                <i className="bi bi-chevron-right ms-1"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}
